// src/app/questionnaire/sections/PersonalDetails.jsx
import React, { useEffect } from "react";
import { hairColourOptions, infectionOptions } from "../config/options";
import { todayISO } from "../config/dateUtils";

export default function PersonalDetails({
  register,
  watch,
  setValue,
  errors,
  showErrors,
}) {

  // Blood-borne infections (multi-select)
  const infections = watch?.("blood_borne_infections") || [];

  // "No" and "Prefer not to say" should be exclusive
  useEffect(() => {
    if (!setValue) return;

    const arr = Array.isArray(infections) ? infections : [];
    const hasNo = arr.includes("No");
    const hasPnts = arr.includes("Prefer not to say");

    if (hasNo && arr.length > 1) {
      setValue("blood_borne_infections", ["No"], { shouldValidate: true });
    }

    if (hasPnts && arr.length > 1) {
      setValue("blood_borne_infections", ["Prefer not to say"], {
        shouldValidate: true,
      });
    }
  }, [infections, setValue]);

  return (
    <section style={styles.section}>
      <h2 style={styles.h2}>Personal details</h2>

      {/* Name + Collector Name */}
      <div style={styles.row}>
        <div style={styles.field}>
          <label style={styles.label}>Name</label>
          <input
            style={styles.input}
            type="text"
            {...register("client_name", { required: "Name is required" })}
            placeholder="Enter name (match ID)"
          />
          {errors?.client_name && (
            <p style={styles.error}>{errors.client_name.message}</p>
          )}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Collector Name</label>
          <input
            style={styles.input}
            type="text"
            {...register("collector_name", {
              required: "Collector name is required",
            })}
            placeholder="Enter collector name"
          />
          {showErrors && errors?.collector_name && (
            <p style={styles.error}>{errors.collector_name.message}</p>
          )}
        </div>
      </div>

      {/* Case Number */}
      <div style={styles.field}>
        <label style={styles.label}>Case Number</label>
        <input
          style={styles.input}
          type="text"
          {...register("case_number", { required: "Case number is required" })}
          placeholder="Enter case number"
        />
        {showErrors && errors?.case_number && (
          <p style={styles.error}>{errors.case_number.message}</p>
        )}
      </div>

      {/* Type of testing */}
      <div style={styles.field}>
        <label style={styles.label}>Type of testing</label>
        <select
          style={styles.input}
          {...register("testing_type", { required: "Type of testing is required" })}
          defaultValue=""
        >
          <option value="">Choose an item</option>
          <option value="Drug & Alcohol">Drug & Alcohol</option>
          <option value="Drug Only">Drug Only</option>
          <option value="Alcohol Only">Alcohol Only</option>
        </select>

        {showErrors && errors?.testing_type && (
          <p style={styles.error}>{errors.testing_type.message}</p>
        )}
      </div>

      {/* Consent */}
      <div style={styles.field}>
        <label style={styles.label}>
          Do you consent to Forensic Testing Service (FTS) asking these questions
          and the completion of the questionnaire?
        </label>

        <div style={styles.inline}>
          <label style={styles.radioLabel}>
            <input
              type="radio"
              value="Yes"
              {...register("consent", { required: "Consent is required" })}
            />
            Yes
          </label>

          <label style={styles.radioLabel}>
            <input
              type="radio"
              value="No"
              {...register("consent", { required: "Consent is required" })}
            />
            No
          </label>
        </div>

        {errors?.consent && <p style={styles.error}>{errors.consent.message}</p>}
      </div>

      {/* DOB + Sex at birth */}
      <div style={styles.row}>
        <div style={styles.field}>
          <label style={styles.label}>Date of Birth</label>
          <input
            style={styles.input}
            type="date"
            max={todayISO}
            {...register("dob", { required: "Date of birth is required" })}
          />
          {errors?.dob && <p style={styles.error}>{errors.dob.message}</p>}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>What was your sex at birth?</label>
          <select
            style={styles.input}
            {...register("sex_at_birth", { required: "Sex at birth is required" })}
            defaultValue=""
          >
            <option value="">Choose an item</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
          </select>
          {errors?.sex_at_birth && (
            <p style={styles.error}>{errors.sex_at_birth.message}</p>
          )}
        </div>
      </div>

      {/* Hair Colour */}
      <div style={styles.field}>
        <label style={styles.label}>
          What is the natural hair colour of your scalp hair? (choose the most applicable)
        </label>
        <select style={styles.input} {...register("natural_hair_colour")}>
          <option value="">Choose an item</option>
          {hairColourOptions.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>

      {/* Blood-borne infections (multi-select) */}
      <div style={styles.field}>
        <label style={styles.label}>
          Do you have any blood-borne infections that you are aware of?
        </label>

        <div style={{ display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8, marginTop: 6 }}>
          {infectionOptions.map((opt) => {
            const isExclusive = opt === "No" || opt === "Prefer not to say";
            const selected = Array.isArray(infections) ? infections : [];
            const exclusiveSelected =
              selected.includes("No") || selected.includes("Prefer not to say");
            const disabled = exclusiveSelected && !isExclusive;

            return (
              <label
                key={opt}
                style={{ display: "flex", gap: 8, alignItems: "center" }}
              >
                <input
                  type="checkbox"
                  value={opt}
                  disabled={disabled}
                  {...register("blood_borne_infections", {
                    validate: (v) =>
                      (Array.isArray(v) && v.length > 0) ||
                      "Please select at least one option",
                  })}
                />
                {opt}
              </label>
            );
          })}
        </div>

        {errors?.blood_borne_infections && (
          <p style={styles.error}>{errors.blood_borne_infections.message}</p>
        )}
      </div>

      {/* Ethnicity */}
      <div style={styles.row}>
        <div style={styles.field}>
          <label style={styles.label}>Ethnicity</label>

          <select style={styles.input} {...register("ethnicity")} defaultValue="">
            <option value="">Select ethnicity</option>
            <option value="White">White</option>
            <option value="Black, Black British, Caribbean or African">
              Black, Black British, Caribbean or African
            </option>
            <option value="Asian or Asian British">Asian or Asian British</option>
            <option value="Mixed or multiple ethnic groups">
              Mixed or multiple ethnic groups
            </option>
            <option value="Other ethnic group">Other ethnic group</option>
            <option value="Prefer not to say">Prefer not to say</option>
          </select>
        </div>
      </div>
    </section>
  );
}

const styles = {
  section: {
    border: "1px solid #ddd",
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
  },
  h2: { margin: 0, marginBottom: 8, color: "#904369" },
  row: { display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 10 },
  field: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 260,
    marginBottom: 10,
  },
  label: { fontWeight: 600, marginBottom: 5, lineHeight: 1.1 },
  input: {
    padding: 9,
    borderRadius: 6,
    border: "1px solid #ccc",
    fontSize: 12,
    boxSizing: "border-box",
  },
  inline: { display: "flex", gap: 14, marginTop: 6 },
  radioLabel: { display: "flex", gap: 6, alignItems: "center" },
  error: { color: "crimson", marginTop: 4, marginBottom: 0, fontSize: 12 },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 12,
    marginBottom: 10,
  },

  fieldGrid: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
};
